import { inject, Injectable } from "@angular/core";
import { EventSpace, EventSpaceRequestBody } from "@models/space";
import { firstValueFrom } from "rxjs";
import { SpaceManagementService } from "./space-management.service";

export function spaceRequest(space: EventSpace): EventSpaceRequestBody {
  return {
    title: space.title,
    description: space.description,
    quantity: Number(space.quantity),
    price: Number(space.price),
    consumption: Number(space.consumption),
    sale_start: space.sale_start,
    sale_end: space.sale_end,
    availability: space.availability,
  };
}

export function sameSpaceData(a: EventSpace, b: EventSpace): boolean {
  const comparable = (space: EventSpace) => ({
    ...spaceRequest(space),
    sale_start: new Date(space.sale_start).getTime(),
    sale_end: new Date(space.sale_end).getTime(),
  });
  return JSON.stringify(comparable(a)) === JSON.stringify(comparable(b));
}

export interface SpaceSyncResult {
  spaces: EventSpace[];
  persisted: EventSpace[];
  duplicates: number;
  failures: string[];
}

@Injectable({ providedIn: "root" })
export class EventSpacesSyncService {
  private readonly api = inject(SpaceManagementService);

  async sync(
    eventId: number,
    current: EventSpace[],
    original: EventSpace[],
  ): Promise<SpaceSyncResult> {
    const spaces: EventSpace[] = [];
    const result: SpaceSyncResult = {
      spaces,
      persisted: original.map((s) => ({ ...s })),
      duplicates: 0,
      failures: [],
    };
    for (const space of current) {
      const sameId = space.id
        ? spaces.find((s) => s.id === space.id)
        : undefined;
      if (sameId && !sameSpaceData(sameId, space)) {
        result.failures.push(
          `Camarote "${space.title}": há duas edições diferentes do mesmo espaço.`,
        );
        return { ...result, spaces: current };
      }
      // Different persisted IDs are distinct records even when their data match.
      const duplicate = spaces.findIndex(
        (s) =>
          (!s.id || !space.id || s.id === space.id) && sameSpaceData(s, space),
      );
      if (duplicate >= 0) {
        if (space.id) spaces[duplicate] = { ...space };
        result.duplicates++;
      } else spaces.push({ ...space });
    }
    if (!spaces.length && !original.length) return result;

    let remote: EventSpace[];
    try {
      remote = await firstValueFrom(this.api.getSpaces(eventId));
    } catch {
      result.failures.push(
        "Não foi possível conferir os camarotes existentes. Nenhum camarote foi enviado.",
      );
      return result;
    }
    const baseline = new Map(
      original.filter((s) => s.id).map((s) => [s.id!, { ...s }]),
    );
    // Recover IDs before computing deletions or creating previously saved drafts.
    for (let i = 0; i < spaces.length; i++) {
      if (!spaces[i].id) {
        const saved = remote.find((s) => s.id && sameSpaceData(s, spaces[i]));
        if (saved) {
          spaces[i] = { ...saved };
          baseline.set(saved.id!, { ...saved });
        }
      }
    }
    const unique = spaces.filter(
      (s, i) => !s.id || spaces.findIndex((other) => other.id === s.id) === i,
    );
    result.duplicates += spaces.length - unique.length;
    result.spaces = unique;

    for (const saved of original) {
      if (saved.id && !unique.some((s) => s.id === saved.id)) {
        try {
          await firstValueFrom(this.api.deleteSpace(saved.id));
          baseline.delete(saved.id);
        } catch {
          result.failures.push(`Remover camarote "${saved.title}"`);
        }
      }
    }
    const uncertainCreates: { index: number; space: EventSpace }[] = [];
    for (let i = 0; i < unique.length; i++) {
      const space = unique[i];
      const saved = space.id ? baseline.get(space.id) : undefined;
      if (saved && sameSpaceData(saved, space)) continue;
      try {
        const response = await firstValueFrom(
          space.id
            ? this.api.updateSpace(space.id, spaceRequest(space))
            : this.api.createSpace(eventId, spaceRequest(space)),
        );
        const confirmed = { ...space, ...response };
        if (!confirmed.id) throw new Error("Missing saved ID");
        baseline.set(confirmed.id, { ...confirmed });
        if (!sameSpaceData(confirmed, space)) {
          // Keep the requested edit pending even if the API returns 200 with old data.
          unique[i] = { ...space, id: confirmed.id };
          result.failures.push(
            `Confirmar alterações do camarote "${space.title}"`,
          );
          continue;
        }
        unique[i] = confirmed;
      } catch (error) {
        const status = (error as { status?: number })?.status;
        if (
          !space.id &&
          (status === 0 ||
            status === 409 ||
            status === 422 ||
            status === undefined)
        ) {
          uncertainCreates.push({ index: i, space });
        } else result.failures.push(`Salvar camarote "${space.title}"`);
      }
    }
    if (uncertainCreates.length) {
      // A 422 alone is never considered success. Confirm the exact data via GET.
      let confirmed: EventSpace[] = [];
      try {
        confirmed = await firstValueFrom(this.api.getSpaces(eventId));
      } catch {
        /* Keep original failures. */
      }
      for (const pending of uncertainCreates) {
        const saved = confirmed.find(
          (s) => s.id && sameSpaceData(s, pending.space),
        );
        if (saved) {
          unique[pending.index] = { ...saved };
          baseline.set(saved.id!, { ...saved });
        } else result.failures.push(`Salvar camarote "${pending.space.title}"`);
      }
    }
    result.persisted = [...baseline.values()];
    return result;
  }
}
